// Annotative-scale + dynamic-block / representation-override reducer
// cases extracted from geometryReducer.ts as part of the PR-0
// mechanical decomposition.
//
// Note (deps.md risk #6): `header/set-cannoscale` is the only handler
// in the geometry reducer that mutates the *document-level* header
// fields (`cannoscale`, `cannoscaleName`). It lives here because the
// behaviour is an annotative concern semantically; future readers
// looking for header mutations should know this file owns them.
//
// Phase 3b PR #4 — migrated to `ReducerMutationResult` contract via the
// new `applyAnnotativeActionWithMutations` function.
//
// Phase 6 (Track 1 cleanup) — the legacy `applyAnnotativeAction` thin
// wrapper has been removed. `geometryReducer.ts` invokes
// `applyAnnotativeActionWithMutations` directly (since PR #5).
//
// Returns `null` when the dispatched command is not handled here so
// the chain in `geometryReducer.ts` falls through to the next
// sub-reducer.
import {
  annotScaleContextSchema,
  assembleDrawingDocument,
  drawingDocumentSchema,
  patchEntityById,
  type AnnotScaleContext,
  type DrawingDocument,
  type DrawingEntity,
} from "@dxf-ai-editor/drawing-model";
import {
  annoupdateMutation,
  objectScaleAddMutation,
  objectScaleRemoveMutation,
  resetDynamicParameters,
  setDynamicParameter,
} from "@dxf-ai-editor/command-engine";
import type {
  DynamicBlockCommand,
  EditorCommand,
} from "@dxf-ai-editor/contracts";

import {
  materializeScaleAcrossDocument,
  type AnnotativeEntity,
} from "./annoMaterialize";
import {
  NO_MUTATIONS,
  type EntityMutation,
  type ReducerMutationResult,
} from "./types";

// ── PR-7 helpers ──────────────────────────────────────────────────────
//
// Type guard for the entities that carry the new `AnnotScaleContext`
// discriminated-union array (TEXT/MTEXT/HATCH/INSERT/ATTDEF). Mirrors
// `isAnnotativeEntityType` in annoMaterialize.ts but kept private here
// so the materialize module's surface stays focused on its own helpers.
function isAnnotScaleContextEntity(entity: {
  type: string;
}): entity is AnnotativeEntity {
  return (
    entity.type === "Text" ||
    entity.type === "MText" ||
    entity.type === "Hatch" ||
    entity.type === "Block" ||
    entity.type === "Attdef"
  );
}

/**
 * Upsert a context into an entity's `annotativeContexts` array. If a
 * context with the same `scaleName` already exists it is REPLACED;
 * otherwise the new context is APPENDED. Returns the original entity
 * reference when the upsert produced an identical array (idempotent
 * re-runs short-circuit so the caller can avoid an unnecessary parse).
 */
function upsertContextOnEntity(
  entity: AnnotativeEntity,
  context: AnnotScaleContext,
): { entity: AnnotativeEntity; changed: boolean } {
  const existing = entity.annotativeContexts ?? [];
  const idx = existing.findIndex((c) => c.scaleName === context.scaleName);
  if (idx === -1) {
    const next = {
      ...entity,
      annotativeContexts: [...existing, context],
    } as AnnotativeEntity;
    return { entity: next, changed: true };
  }
  // Replace if the existing entry differs by reference / content. We
  // compare via JSON to keep the no-op detection tight — context shapes
  // are small (max ~10 fields) so the stringification cost is trivial
  // versus the gain of avoiding a redundant Zod re-parse on no-op
  // upserts (e.g. Properties palette toggle that lands on the same
  // value).
  const previous = existing[idx];
  if (JSON.stringify(previous) === JSON.stringify(context)) {
    return { entity, changed: false };
  }
  const nextContexts = existing.slice();
  nextContexts[idx] = context;
  const next = {
    ...entity,
    annotativeContexts: nextContexts,
  } as AnnotativeEntity;
  return { entity: next, changed: true };
}

/**
 * Remove the context with the given `scaleName` from an entity. If no
 * matching entry exists the original entity reference is returned
 * (idempotent — caller can detect "no work needed" via referential
 * equality).
 */
function removeContextFromEntity(
  entity: AnnotativeEntity,
  scaleName: string,
): { entity: AnnotativeEntity; changed: boolean } {
  const existing = entity.annotativeContexts ?? [];
  const next = existing.filter((c) => c.scaleName !== scaleName);
  if (next.length === existing.length) {
    return { entity, changed: false };
  }
  const updated = {
    ...entity,
    annotativeContexts: next,
  } as AnnotativeEntity;
  return { entity: updated, changed: true };
}

/**
 * Walk every space's entities AND every block definition's child
 * entities, applying the given `mutator` to the entity matching
 * `entityId`. Used by `entity/add-annotation-context` /
 * `entity/remove-annotation-context` to support both top-level
 * entities and BLOCK children in a single pass — same walk pattern as
 * `materializeScaleAcrossDocument`.
 *
 * Returns the original document reference when the entity is not
 * found, OR when the mutator reported no change (idempotent
 * short-circuit). Returns a new document with the mutated entity
 * spliced into its container otherwise.
 */
function mutateAnnotativeEntityById(
  document: DrawingDocument,
  entityId: string,
  mutator: (
    entity: AnnotativeEntity,
  ) => { entity: AnnotativeEntity; changed: boolean },
): DrawingDocument {
  // Pass 1 — every space's entities array.
  let foundInSpace = false;
  let anySpaceChanged = false;
  const nextSpaces = document.spaces.map((space) => {
    const idx = space.entities.findIndex((e) => e.id === entityId);
    if (idx === -1) return space;
    foundInSpace = true;
    const target = space.entities[idx];
    if (!target || !isAnnotScaleContextEntity(target)) return space;
    const result = mutator(target);
    if (!result.changed) return space;
    anySpaceChanged = true;
    const nextEntities = space.entities.slice();
    nextEntities[idx] = result.entity as (typeof space.entities)[number];
    return { ...space, entities: nextEntities };
  });
  if (foundInSpace) {
    if (!anySpaceChanged) return document;
    return { ...document, spaces: nextSpaces };
  }

  // Pass 2 — block definition children. AutoCAD allows annotative
  // TEXT/MTEXT/INSERT/HATCH inside a BLOCK definition; the Properties
  // palette can target those when the user is in a BEDIT session.
  let anyBlockChanged = false;
  const nextBlocks: typeof document.blocks = {};
  for (const [name, blockDef] of Object.entries(document.blocks)) {
    const idx = blockDef.entities.findIndex((e) => e.id === entityId);
    if (idx === -1) {
      nextBlocks[name] = blockDef;
      continue;
    }
    const target = blockDef.entities[idx];
    if (!target || !isAnnotScaleContextEntity(target)) {
      nextBlocks[name] = blockDef;
      continue;
    }
    const result = mutator(target);
    if (!result.changed) {
      nextBlocks[name] = blockDef;
      continue;
    }
    anyBlockChanged = true;
    const nextEntities = blockDef.entities.slice();
    nextEntities[idx] = result.entity as (typeof blockDef.entities)[number];
    nextBlocks[name] = { ...blockDef, entities: nextEntities };
  }
  if (!anyBlockChanged) return document;
  return { ...document, blocks: nextBlocks };
}

// ── Phase 3b PR #4 — mutation-tracking helpers ───────────────────────
//
// Most cases in this reducer touch a single entity (objectscale-add /
// remove, dynamic/set, dynamic/reset, set-insert-parameter-override,
// clear-insert-parameter-override, set-insert-visibility-state, and the
// add/remove-annotation-context paths route through the same code).
// `singleUpdated` builds the canonical one-entity `EntityMutation` for
// those cases without each callsite open-coding the record shape.
function singleUpdated(
  spaceId: string,
  prev: DrawingEntity,
  next: DrawingEntity,
): EntityMutation {
  return {
    added: [],
    removed: [],
    updated: [{ spaceId, prev, next }],
    styleTablesTouched: new Set(),
  };
}

// `geometry/annoupdate` and `header/set-cannoscale-and-sync` can touch
// many entities in a single transaction. Referential equality across
// `assembleDrawingDocument` is not viable: in dev/test mode the helper
// calls `drawingDocumentSchema.parse(next)` which rebuilds *every*
// entity object, breaking identity globally. Instead callers track
// touched entity ids explicitly during the in-flight transform (or
// derive them from helper bookkeeping like `affectedEntityCount` /
// the materializer's pre-parse output). `diffByIds` then extracts
// prev/next pairs by id for those tracked ids.
//
// `touchedIds` excludes entities living inside block-definition
// children — those aren't tracked by the entity index, so they
// naturally surface as NO_MUTATIONS at entity level (the renderer
// invalidates blocks via the cache key path).
function diffByIds(
  prev: DrawingDocument,
  next: DrawingDocument,
  touchedIds: ReadonlySet<string>,
): EntityMutation {
  if (touchedIds.size === 0) return NO_MUTATIONS;
  const prevById = new Map<string, { spaceId: string; entity: DrawingEntity }>();
  for (const space of prev.spaces) {
    for (const entity of space.entities) {
      prevById.set(entity.id, { spaceId: space.id, entity });
    }
  }
  const nextById = new Map<string, { spaceId: string; entity: DrawingEntity }>();
  for (const space of next.spaces) {
    for (const entity of space.entities) {
      nextById.set(entity.id, { spaceId: space.id, entity });
    }
  }
  const updated: Array<{ spaceId: string; prev: DrawingEntity; next: DrawingEntity }> = [];
  for (const id of touchedIds) {
    const p = prevById.get(id);
    const n = nextById.get(id);
    if (!p || !n) continue; // block-child or removed entity — skip.
    updated.push({ spaceId: n.spaceId, prev: p.entity, next: n.entity });
  }
  if (updated.length === 0) return NO_MUTATIONS;
  return {
    added: [],
    removed: [],
    updated,
    styleTablesTouched: new Set(),
  };
}

/**
 * Compute the touched-entity-id set produced by
 * `materializeScaleAcrossDocument` by comparing space-entities pairwise
 * against the source document. The materializer preserves entity refs
 * for entities it didn't touch, so reference inequality is the
 * authoritative signal at this layer (BEFORE the final
 * `assembleDrawingDocument` parse). Block-children are skipped.
 */
function materializerTouchedIds(
  source: DrawingDocument,
  materialized: DrawingDocument,
): Set<string> {
  const touched = new Set<string>();
  if (source === materialized) return touched;
  for (let i = 0; i < source.spaces.length; i++) {
    const sourceSpace = source.spaces[i]!;
    const nextSpace = materialized.spaces[i];
    if (!nextSpace || sourceSpace === nextSpace) continue;
    const prevById = new Map(sourceSpace.entities.map((e) => [e.id, e] as const));
    for (const entity of nextSpace.entities) {
      const prevEntity = prevById.get(entity.id);
      if (prevEntity && prevEntity !== entity) touched.add(entity.id);
    }
  }
  return touched;
}

/**
 * Phase 3b PR #4 — mutation-tracking entry point. Returns the resolved
 * document AND populates `mutations` so the dispatch layer can drive
 * incremental cache + index updates in O(|touched|).
 *
 * Returns `null` when the command is not handled here (sub-reducer
 * fall-through). The legacy `applyAnnotativeAction` thin wrapper was
 * removed in Phase 6; callers consume `result.document` directly.
 */
export function applyAnnotativeActionWithMutations(
  geometryDocument: DrawingDocument,
  command: EditorCommand | DynamicBlockCommand,
): ReducerMutationResult | null {
  switch (command.type) {
    // ── Phase FIN.2 — annotative blocks ─────────────────────────────
    case "header/set-cannoscale": {
      const found = geometryDocument.annotationScales.find(
        (s) => s.name === command.cannoscaleName,
      );
      const factor = found?.scaleFactor ?? geometryDocument.cannoscale;
      const document = assembleDrawingDocument({
        ...geometryDocument,
        cannoscale: factor,
        cannoscaleName: command.cannoscaleName,
      });
      // Header-only mutation — entity payloads are unchanged but the
      // assemble call rebuilds the document. No entity-level mutation
      // to report; consumers that need a header-resync hook on this
      // command rely on the legacy allowlist (Phase 2).
      return { document, mutations: NO_MUTATIONS };
    }

    // PR-6 — `header/set-cannoscale-and-sync` is the AutoCAD-faithful
    // CANNOSCALE switch dispatched by the AnnotationStatusBar dropdown.
    // Performs the cannoscale update PLUS the ANNOAUTOSCALE
    // materialization pass in a single immutable update so the history
    // stack records exactly one undo step (the AutoCAD contract).
    //
    // Decision matrix:
    //   - scale not in annotationScales[]: state unchanged. The
    //     reducer cannot fabricate a scaleFactor without a catalogue
    //     entry; surfacing this as a no-op matches AutoCAD's
    //     "Unknown scale" rejection at the command line. The audit
    //     channel is the shell reducer's responsibility — emitting
    //     from the geometry reducer would couple it to the audit log
    //     shape (which lives on the shell state).
    //   - autoMaterialize=false OR annoAutoScale<=0: skip
    //     materialization. The header still flips to the new scale.
    //   - otherwise: materialize via materializeScaleAcrossDocument.
    case "header/set-cannoscale-and-sync": {
      const found = geometryDocument.annotationScales.find(
        (s) => s.name === command.cannoscaleName,
      );
      if (!found) {
        // Unknown scale — leave the document unchanged. The status-bar
        // dropdown only offers entries from annotationScales[], so this
        // path is reachable only via raw command-line dispatch with a
        // typo'd name. Returning the original document keeps the undo
        // history clean.
        return { document: geometryDocument, mutations: NO_MUTATIONS };
      }
      // First step: flip the header. We do this before deciding whether
      // to materialize so a caller passing autoMaterialize=false still
      // gets the cannoscale change persisted.
      const headerOnly: typeof geometryDocument = {
        ...geometryDocument,
        cannoscale: found.scaleFactor,
        cannoscaleName: command.cannoscaleName,
      };
      const shouldMaterialize =
        command.autoMaterialize !== false &&
        (geometryDocument.annoAutoScale ?? 4) > 0;
      if (!shouldMaterialize) {
        // Header-only flip; no entity payloads change.
        const document = assembleDrawingDocument(headerOnly);
        return { document, mutations: NO_MUTATIONS };
      }
      // Second step: fan-out per-scale contexts to every annotative
      // entity that lacks one. The materializer returns the SAME
      // document reference when no entity needed a context (idempotent
      // re-runs short-circuit), in which case we still need to validate
      // the header-only mutation through Zod.
      const result = materializeScaleAcrossDocument(headerOnly, {
        name: command.cannoscaleName,
        scaleFactor: found.scaleFactor,
      });
      // Track touched ids BEFORE the final Zod parse — the materializer
      // preserves entity references for entities it didn't touch, so
      // reference inequality is the authoritative signal here. Using
      // `headerOnly` (not `geometryDocument`) as the source so that the
      // header-spread doesn't get treated as a mutation.
      const touchedIds = materializerTouchedIds(headerOnly, result.document);
      const document = assembleDrawingDocument(result.document);
      const mutations = diffByIds(geometryDocument, document, touchedIds);
      return { document, mutations };
    }

    // PR-7 — `geometry/annoupdate` is now a THREE-PASS materialization
    // pipeline that runs as a SINGLE undoable transaction (one immutable
    // update, one Zod parse at the end). AutoCAD's ANNOUPDATE command
    // walks every annotative entity and synchronizes its current
    // representation to the active CANNOSCALE — for INSERTs this means
    // scaleX/scaleY rewrite (legacy `annoupdateMutation`), for
    // TEXT/MTEXT/HATCH/INSERT/ATTDEF this means materializing the per-
    // scale `AnnotScaleContext` if missing (PR-6 helper), and for MLEADER
    // it means appending a legacy `mleaderContextDataSchema` entry
    // derived from the master text/arrow/dogleg fields if missing.
    //
    // Single-undo contract: AutoCAD records ONE history step per
    // ANNOUPDATE invocation regardless of how many entities were
    // touched. We honour that by merging all three passes into a single
    // `assembleDrawingDocument` at the end. Re-running ANNOUPDATE
    // back-to-back is a no-op for already-synced entities (each pass
    // checks "already has it" before mutating).
    case "geometry/annoupdate": {
      // ── Pass 1: INSERT scaleX/scaleY rewrite (legacy behaviour). ──
      const inserts: Array<{
        id: string;
        spaceId: string;
        blockName: string;
        scaleX: number;
        scaleY: number;
      }> = [];
      for (const space of geometryDocument.spaces) {
        for (const entity of space.entities) {
          if (entity.type !== "Block") continue;
          inserts.push({
            id: entity.id,
            spaceId: entity.spaceId,
            blockName: entity.blockName,
            scaleX: entity.scaleX,
            scaleY: entity.scaleY,
          });
        }
      }
      const insertResult = annoupdateMutation({
        blockRecords: geometryDocument.blockRecords,
        cannoscale: geometryDocument.cannoscale,
        inserts,
      });
      const insertUpdates = new Map(
        insertResult.updates.map((u) => [u.insertId, u]),
      );

      // ── Pass 2: MLEADER legacy-context append. ──
      // MLEADER carries its own `mleaderContextDataSchema` array (NOT
      // the new `AnnotScaleContext` discriminated union — see model.ts
      // line 432). Append a derived entry when no entry matches the
      // current cannoscaleName. The shape mirrors
      // `mleaderContextDataSchema` exactly (model.ts:373-383): `scale`
      // (legacy field name — NOT `scaleFactor`), `scaleName`,
      // `textHeight`, `arrowheadSize`, `doglegLength`, `landingGap`,
      // `textRotation`, `textColor`, `textWidth`. Defaults match the
      // schema so a master with all-defaults yields a context Zod
      // accepts without modification.
      const cannoscaleName = geometryDocument.cannoscaleName;
      const cannoscale = geometryDocument.cannoscale;

      // Walk pass that combines Pass 1 (INSERT scaleX/scaleY) with
      // Pass 2 (MLEADER context append). Building both in the same map
      // keeps the spaces/entities walk to ONE pass instead of three.
      // Tracks whether ANY space changed so the reducer can short-
      // circuit the no-op case (re-running ANNOUPDATE on an already-
      // synced document must return the original document reference —
      // verified by the "idempotent" test).
      let anySpaceChanged = false;
      // PR #4 mutation contract — track Pass 1+2 touched ids so the
      // dispatch layer can apply incremental updates per entity. Pass 3
      // ids are collected separately via `materializerTouchedIds` below.
      const annoupdateTouchedIds = new Set<string>();
      const nextSpaces = geometryDocument.spaces.map((space) => {
        let spaceChanged = false;
        const nextEntities = space.entities.map((entity) => {
          // Pass 1 — INSERT scale rewrite.
          if (entity.type === "Block") {
            const u = insertUpdates.get(entity.id);
            if (!u) return entity;
            spaceChanged = true;
            annoupdateTouchedIds.add(entity.id);
            return { ...entity, scaleX: u.newScaleX, scaleY: u.newScaleY };
          }
          // Pass 2 — MLEADER legacy context append.
          if (entity.type === "Multileader") {
            const hasContext = entity.annotativeContexts.some(
              (c) => c.scaleName === cannoscaleName,
            );
            if (hasContext) return entity;
            const derivedContext = {
              scaleName: cannoscaleName,
              scale: cannoscale,
              textHeight: entity.textHeight,
              arrowheadSize: entity.arrowheadSize,
              doglegLength: entity.doglegLength,
              landingGap: entity.landingGap,
              textRotation: entity.textRotation,
              textColor: entity.textColor,
              textWidth: entity.textWidth,
            };
            spaceChanged = true;
            annoupdateTouchedIds.add(entity.id);
            return {
              ...entity,
              annotativeContexts: [
                ...entity.annotativeContexts,
                derivedContext,
              ],
            };
          }
          return entity;
        });
        if (!spaceChanged) return space;
        anySpaceChanged = true;
        return { ...space, entities: nextEntities };
      });

      // ── Pass 3: TEXT/MTEXT/HATCH/INSERT/ATTDEF context materialize. ──
      // Reuse PR-6 `materializeScaleAcrossDocument` against the
      // intermediate document (after Pass 1+2). The materializer skips
      // MLEADER (legacy schema, handled above) and DIMENSION (PR-10
      // owns DimStyle Manager + dimension annotative wiring per plan
      // §8). It also walks block-children so nested annotative entities
      // pick up contexts. Build the intermediate document only when
      // Pass 1+2 actually mutated something — otherwise pass the
      // original through so referential-equality short-circuits stay
      // intact for the idempotent re-run case.
      const intermediateDocument: DrawingDocument = anySpaceChanged
        ? {
            ...geometryDocument,
            spaces: nextSpaces,
          }
        : geometryDocument;
      // No-op short-circuit: when there is no annotation scale entry
      // matching the current cannoscaleName the materializer cannot
      // derive a `scaleFactor` for fresh contexts (annotationScales[]
      // is the source of truth). Skip Pass 3 in that case so the
      // function still returns the Pass 1+2 mutations rather than
      // silently dropping them.
      const scaleEntry = geometryDocument.annotationScales.find(
        (s) => s.name === cannoscaleName,
      );
      const materializedResult = scaleEntry
        ? materializeScaleAcrossDocument(intermediateDocument, {
            name: scaleEntry.name,
            scaleFactor: scaleEntry.scaleFactor,
          })
        : { document: intermediateDocument, affectedEntityCount: 0 };
      const materializedDocument = materializedResult.document;

      // Single-undo contract: ONE Zod parse covering all three passes.
      // No-op detection: Pass 1+2 ran without mutating anything (anySpaceChanged
      // is false) AND the materializer reported zero affected entities.
      if (!anySpaceChanged && materializedResult.affectedEntityCount === 0) {
        return { document: geometryDocument, mutations: NO_MUTATIONS };
      }
      // Pass 3 touched ids — derive from materializer's pre-parse output
      // (intermediate vs materialized.document; materializer preserves
      // refs for entities it didn't touch).
      const pass3TouchedIds = materializerTouchedIds(
        intermediateDocument,
        materializedResult.document,
      );
      // Combine Pass 1+2 (annoupdateTouchedIds) with Pass 3
      // (pass3TouchedIds) into the full set of entities touched by the
      // ANNOUPDATE transaction.
      const touchedIds = new Set<string>([
        ...annoupdateTouchedIds,
        ...pass3TouchedIds,
      ]);
      const document = assembleDrawingDocument(materializedDocument);
      const mutations = diffByIds(geometryDocument, document, touchedIds);
      return { document, mutations };
    }

    case "geometry/objectscale-add": {
      let didChange = false;
      const next = patchEntityById(geometryDocument, command.insertId, {});
      const space = next.spaces.find((s) =>
        s.entities.some((e) => e.id === command.insertId),
      );
      if (!space) return { document: geometryDocument, mutations: NO_MUTATIONS };
      const entity = space.entities.find((e) => e.id === command.insertId);
      if (!entity || entity.type !== "Block")
        return { document: geometryDocument, mutations: NO_MUTATIONS };
      const result = objectScaleAddMutation({
        insertId: command.insertId,
        scaleName: command.scaleName,
        currentScales: entity.annotationScales,
      });
      if (result.newScales === entity.annotationScales)
        return { document: geometryDocument, mutations: NO_MUTATIONS };
      didChange = true;
      if (!didChange) return { document: geometryDocument, mutations: NO_MUTATIONS };
      const document = assembleDrawingDocument({
        ...geometryDocument,
        spaces: geometryDocument.spaces.map((s) => ({
          ...s,
          entities: s.entities.map((e) => {
            if (e.id !== command.insertId || e.type !== "Block") return e;
            return { ...e, annotationScales: result.newScales };
          }),
        })),
      });
      // Single-entity update: pull the post-parse next entity by id +
      // space so the `prev` and `next` come from a coherent pair.
      const nextSpace = document.spaces.find((s) => s.id === space.id);
      const nextEntity = nextSpace?.entities.find(
        (e) => e.id === command.insertId,
      );
      const mutations = nextEntity
        ? singleUpdated(space.id, entity, nextEntity)
        : NO_MUTATIONS;
      return { document, mutations };
    }

    // ── PR-4 — Annotation status-bar toggles ────────────────────────
    // Each toggle reads the current header value and writes the
    // inverse. ANNOAUTOSCALE has sign-flip semantics per AutoCAD spec
    // (range -4..4): positive = ON, negative or 0 = OFF; toggling
    // preserves the magnitude when going ON→OFF (for "restore prior
    // state when re-enabled" parity) and snaps to +4 when going OFF→ON
    // from a 0/negative state.
    case "header/toggle-annoallvisible": {
      const document = assembleDrawingDocument({
        ...geometryDocument,
        annoAllVisible: !geometryDocument.annoAllVisible,
      });
      return { document, mutations: NO_MUTATIONS };
    }

    case "header/toggle-annoautoscale": {
      const cur = geometryDocument.annoAutoScale ?? 4;
      const next = cur > 0 ? -Math.abs(cur) : 4;
      const document = assembleDrawingDocument({
        ...geometryDocument,
        annoAutoScale: next,
      });
      return { document, mutations: NO_MUTATIONS };
    }

    case "header/toggle-annomonitor": {
      const document = assembleDrawingDocument({
        ...geometryDocument,
        annoMonitor: !geometryDocument.annoMonitor,
      });
      return { document, mutations: NO_MUTATIONS };
    }

    // ── PR-9 — SCALELISTEDIT scale-list catalogue mutations ──────────
    // `header/scalelist-add` appends an entry to `annotationScales[]`.
    // Idempotent: if an entry with the same `scaleName` already exists
    // we return the original document reference (no-op). The new entry
    // surfaces in the AnnotationStatusBar dropdown immediately and is
    // persisted through DXF export.
    case "header/scalelist-add": {
      const existing = geometryDocument.annotationScales ?? [];
      if (existing.some((s) => s.name === command.scaleName)) {
        return { document: geometryDocument, mutations: NO_MUTATIONS };
      }
      const next = [
        ...existing,
        {
          name: command.scaleName,
          scaleFactor: command.scaleFactor,
          isHidden: command.isHidden,
        },
      ];
      const document = assembleDrawingDocument({
        ...geometryDocument,
        annotationScales: next,
      });
      return { document, mutations: NO_MUTATIONS };
    }

    // `header/scalelist-remove` filters the matching entry out of the
    // catalogue. Idempotent: if no entry matches `scaleName` we return
    // the original document reference (no-op). Side-effect: when the
    // removed scale was the current `cannoscaleName`, the active scale
    // resets to the first remaining entry (or "1:1" + factor 1 when the
    // catalogue becomes empty) so the controlled dropdown does not
    // dangle on a non-existent value.
    case "header/scalelist-remove": {
      const existing = geometryDocument.annotationScales ?? [];
      const idx = existing.findIndex((s) => s.name === command.scaleName);
      if (idx === -1)
        return { document: geometryDocument, mutations: NO_MUTATIONS };
      const next = existing.filter((s) => s.name !== command.scaleName);
      // Reset cannoscale if the removed entry was active.
      let nextCannoscaleName = geometryDocument.cannoscaleName;
      let nextCannoscale = geometryDocument.cannoscale;
      if (geometryDocument.cannoscaleName === command.scaleName) {
        const fallback = next[0];
        if (fallback) {
          nextCannoscaleName = fallback.name;
          nextCannoscale = fallback.scaleFactor;
        } else {
          nextCannoscaleName = "1:1";
          nextCannoscale = 1;
        }
      }
      const document = assembleDrawingDocument({
        ...geometryDocument,
        annotationScales: next,
        cannoscaleName: nextCannoscaleName,
        cannoscale: nextCannoscale,
      });
      return { document, mutations: NO_MUTATIONS };
    }

    case "geometry/objectscale-remove": {
      const space = geometryDocument.spaces.find((s) =>
        s.entities.some((e) => e.id === command.insertId),
      );
      if (!space) return { document: geometryDocument, mutations: NO_MUTATIONS };
      const entity = space.entities.find((e) => e.id === command.insertId);
      if (!entity || entity.type !== "Block")
        return { document: geometryDocument, mutations: NO_MUTATIONS };
      const result = objectScaleRemoveMutation({
        insertId: command.insertId,
        scaleName: command.scaleName,
        currentScales: entity.annotationScales,
      });
      if (result.newScales === entity.annotationScales)
        return { document: geometryDocument, mutations: NO_MUTATIONS };
      const document = assembleDrawingDocument({
        ...geometryDocument,
        spaces: geometryDocument.spaces.map((s) => ({
          ...s,
          entities: s.entities.map((e) => {
            if (e.id !== command.insertId || e.type !== "Block") return e;
            return { ...e, annotationScales: result.newScales };
          }),
        })),
      });
      const nextSpace = document.spaces.find((s) => s.id === space.id);
      const nextEntity = nextSpace?.entities.find(
        (e) => e.id === command.insertId,
      );
      const mutations = nextEntity
        ? singleUpdated(space.id, entity, nextEntity)
        : NO_MUTATIONS;
      return { document, mutations };
    }

    // ── PR-7 — universal per-entity OBJECTSCALE add / remove ──────────
    // These wire the OBJECTSCALE command (PR-8) and the Properties
    // palette "Annotative Object Scale" dialog (PR-9) to mutate the
    // `annotativeContexts[]` array of any annotative-capable entity
    // (TEXT/MTEXT/HATCH/INSERT/ATTDEF). MLEADER and DIMENSION carry
    // legacy schema arrays whose shape predates the
    // `AnnotScaleContext` discriminated union — they are intentionally
    // skipped here (PR-10 owns the DimStyle / MLeaderStyle Manager
    // wiring for those legacy arrays).
    //
    // Shape contract: the contracts package keeps the `context` field
    // typed as `z.unknown()` to avoid coupling to drawing-model. We
    // validate via `annotScaleContextSchema.parse` here so a bad
    // payload fails fast with a clear Zod error instead of corrupting
    // the document via the final `assembleDrawingDocument`.
    //
    // Idempotency:
    //   - add: upsert semantics — replace existing context with same
    //     scaleName, OR append if absent. Re-dispatching with an
    //     identical context returns the original document reference.
    //   - remove: idempotent — no-op when no entry matches scaleName.
    case "entity/add-annotation-context": {
      // Validate the unknown payload via the discriminated-union
      // schema. Failure throws a ZodError caught by the reducer's
      // outer try/catch (or surfaces to the caller — same path as
      // every other reducer Zod parse).
      const parsedContext = annotScaleContextSchema.parse(command.context);
      const next = mutateAnnotativeEntityById(
        geometryDocument,
        command.entityId,
        (entity) => upsertContextOnEntity(entity, parsedContext),
      );
      if (next === geometryDocument)
        return { document: geometryDocument, mutations: NO_MUTATIONS };
      const document = assembleDrawingDocument(next);
      // The mutation may have landed on a top-level space entity OR on
      // a block-definition child. `diffByIds` only resolves top-level
      // entities, so block-children naturally surface as NO_MUTATIONS
      // at entity level — that's correct: block-children aren't
      // tracked by the entity index.
      const mutations = diffByIds(
        geometryDocument,
        document,
        new Set([command.entityId]),
      );
      return { document, mutations };
    }

    case "entity/remove-annotation-context": {
      const next = mutateAnnotativeEntityById(
        geometryDocument,
        command.entityId,
        (entity) => removeContextFromEntity(entity, command.scaleName),
      );
      if (next === geometryDocument)
        return { document: geometryDocument, mutations: NO_MUTATIONS };
      const document = assembleDrawingDocument(next);
      const mutations = diffByIds(
        geometryDocument,
        document,
        new Set([command.entityId]),
      );
      return { document, mutations };
    }

    // ── DYNLIST / DYNSET / DYNRESET (INSERT customProperties CLI) ──
    // `dynamic/list` is read-only — the document is unchanged; the
    // shell reducer alone logs the formatted transcript. `dynamic/set`
    // and `dynamic/reset` mutate the target INSERT's customProperties
    // via the pure helpers in command-engine. The evaluator re-runs
    // automatically on the next render via the cache-invalidation path
    // in `scene/blockRender.tsx` (cache key includes stable-stringified
    // customProperties — flipping a value yields a cache miss, which
    // re-invokes `evaluateBlock` → `filterVisibleEntities`). Dynamic-
    // metadata blocks see an SVG-level geometry delta; static blocks
    // and fallback raw-groups blocks without typed metadata short-
    // circuit to their static entity list.
    case "dynamic/list":
      // Read-only — document is unchanged. Caller must compare on
      // identity to detect "no work" (same as the legacy contract).
      return { document: geometryDocument, mutations: NO_MUTATIONS };

    case "dynamic/set": {
      const space = geometryDocument.spaces.find((s) =>
        s.entities.some((e) => e.id === command.entityId),
      );
      if (!space) return { document: geometryDocument, mutations: NO_MUTATIONS };
      const entity = space.entities.find((e) => e.id === command.entityId);
      if (!entity || entity.type !== "Block")
        return { document: geometryDocument, mutations: NO_MUTATIONS };
      const blockDef = geometryDocument.blocks[entity.blockName];
      const result = setDynamicParameter(
        entity,
        blockDef,
        command.paramName,
        command.rawValue,
      );
      if (!result.ok)
        return { document: geometryDocument, mutations: NO_MUTATIONS };
      const document = assembleDrawingDocument({
        ...geometryDocument,
        spaces: geometryDocument.spaces.map((s) =>
          s.id === space.id
            ? {
                ...s,
                entities: s.entities.map((e) =>
                  e.id === command.entityId ? result.entity : e,
                ),
              }
            : s,
        ),
      });
      const nextSpace = document.spaces.find((s) => s.id === space.id);
      const nextEntity = nextSpace?.entities.find(
        (e) => e.id === command.entityId,
      );
      const mutations = nextEntity
        ? singleUpdated(space.id, entity, nextEntity)
        : NO_MUTATIONS;
      return { document, mutations };
    }

    case "dynamic/reset": {
      const space = geometryDocument.spaces.find((s) =>
        s.entities.some((e) => e.id === command.entityId),
      );
      if (!space) return { document: geometryDocument, mutations: NO_MUTATIONS };
      const entity = space.entities.find((e) => e.id === command.entityId);
      if (!entity || entity.type !== "Block")
        return { document: geometryDocument, mutations: NO_MUTATIONS };
      const blockDef = geometryDocument.blocks[entity.blockName];
      const result = resetDynamicParameters(entity, blockDef);
      if (!result.ok)
        return { document: geometryDocument, mutations: NO_MUTATIONS };
      const document = assembleDrawingDocument({
        ...geometryDocument,
        spaces: geometryDocument.spaces.map((s) =>
          s.id === space.id
            ? {
                ...s,
                entities: s.entities.map((e) =>
                  e.id === command.entityId ? result.entity : e,
                ),
              }
            : s,
        ),
      });
      const nextSpace = document.spaces.find((s) => s.id === space.id);
      const nextEntity = nextSpace?.entities.find(
        (e) => e.id === command.entityId,
      );
      const mutations = nextEntity
        ? singleUpdated(space.id, entity, nextEntity)
        : NO_MUTATIONS;
      return { document, mutations };
    }

    // Phase N.4 — per-INSERT representation override commands.
    // Distinct from dynamic/set in that they ALSO record the divergence
    // on the typed `representationOverrides.parameterOverrides` map so
    // the DXF writer emits AcDbBlockRepresentationData. The runtime
    // value still lives on `customProperties` (kept in sync for the
    // evaluator).
    case "geometry/set-insert-parameter-override": {
      const space = geometryDocument.spaces.find((s) =>
        s.entities.some((e) => e.id === command.entityId),
      );
      if (!space) return { document: geometryDocument, mutations: NO_MUTATIONS };
      const entity = space.entities.find((e) => e.id === command.entityId);
      if (!entity || entity.type !== "Block")
        return { document: geometryDocument, mutations: NO_MUTATIONS };
      const blockDef = geometryDocument.blocks[entity.blockName];
      // Re-use setDynamicParameter for the value parsing + customProperties
      // update (handles compound params, type coercion, infoMap lookups).
      const result = setDynamicParameter(
        entity,
        blockDef,
        command.paramName,
        command.rawValue,
      );
      if (!result.ok)
        return { document: geometryDocument, mutations: NO_MUTATIONS };
      // Mirror the divergence onto representationOverrides — typed
      // structure that the bridge writes out as AcDbBlockRepresentationData.
      const insert = result.entity;
      const existingOverrides = insert.representationOverrides;
      const nextParameterOverrides = {
        ...(existingOverrides?.parameterOverrides ?? {}),
        [command.paramName]:
          insert.customProperties[command.paramName] ?? command.rawValue,
      };
      const nextOverrides: NonNullable<
        typeof insert.representationOverrides
      > = {
        ...existingOverrides,
        parameterOverrides: nextParameterOverrides,
      };
      const updatedInsert = {
        ...insert,
        representationOverrides: nextOverrides,
      };
      const document = assembleDrawingDocument({
        ...geometryDocument,
        spaces: geometryDocument.spaces.map((s) =>
          s.id === space.id
            ? {
                ...s,
                entities: s.entities.map((e) =>
                  e.id === command.entityId ? updatedInsert : e,
                ),
              }
            : s,
        ),
      });
      const nextSpace = document.spaces.find((s) => s.id === space.id);
      const nextEntity = nextSpace?.entities.find(
        (e) => e.id === command.entityId,
      );
      const mutations = nextEntity
        ? singleUpdated(space.id, entity, nextEntity)
        : NO_MUTATIONS;
      return { document, mutations };
    }

    case "geometry/clear-insert-parameter-override": {
      const space = geometryDocument.spaces.find((s) =>
        s.entities.some((e) => e.id === command.entityId),
      );
      if (!space) return { document: geometryDocument, mutations: NO_MUTATIONS };
      const entity = space.entities.find((e) => e.id === command.entityId);
      if (!entity || entity.type !== "Block")
        return { document: geometryDocument, mutations: NO_MUTATIONS };
      const existingOverrides = entity.representationOverrides;
      if (!existingOverrides)
        return { document: geometryDocument, mutations: NO_MUTATIONS };
      // Drop the param from the override map. The runtime customProperties
      // value is left to subsequent dynamic/reset to reset to the block
      // def default — clearing the override only marks "no longer
      // diverging from default" on the persistence side.
      const remaining = { ...existingOverrides.parameterOverrides };
      delete remaining[command.paramName];
      const stillHasOverrides =
        Object.keys(remaining).length > 0 ||
        existingOverrides.currentVisibilityStateId !== undefined ||
        existingOverrides.representationEtag !== undefined ||
        existingOverrides.representationDataHandle !== undefined;
      const nextOverrides = stillHasOverrides
        ? { ...existingOverrides, parameterOverrides: remaining }
        : undefined;
      const updatedInsert = {
        ...entity,
        ...(nextOverrides
          ? { representationOverrides: nextOverrides }
          : { representationOverrides: undefined }),
      };
      const document = assembleDrawingDocument({
        ...geometryDocument,
        spaces: geometryDocument.spaces.map((s) =>
          s.id === space.id
            ? {
                ...s,
                entities: s.entities.map((e) =>
                  e.id === command.entityId ? updatedInsert : e,
                ),
              }
            : s,
        ),
      });
      const nextSpace = document.spaces.find((s) => s.id === space.id);
      const nextEntity = nextSpace?.entities.find(
        (e) => e.id === command.entityId,
      );
      const mutations = nextEntity
        ? singleUpdated(space.id, entity, nextEntity)
        : NO_MUTATIONS;
      return { document, mutations };
    }

    case "geometry/set-insert-visibility-state": {
      const space = geometryDocument.spaces.find((s) =>
        s.entities.some((e) => e.id === command.entityId),
      );
      if (!space) return { document: geometryDocument, mutations: NO_MUTATIONS };
      const entity = space.entities.find((e) => e.id === command.entityId);
      if (!entity || entity.type !== "Block")
        return { document: geometryDocument, mutations: NO_MUTATIONS };
      const existingOverrides = entity.representationOverrides;
      const trimmed = command.stateId.trim();
      // Update both the runtime visibilityState (consumed by the
      // evaluator + filterVisibleEntities) AND the typed override
      // structure (consumed by the DXF writer).
      let nextOverrides: typeof existingOverrides;
      if (trimmed === "") {
        // Empty stateId = clear the override. Strip the field from
        // representationOverrides; if the structure becomes empty drop
        // the whole record.
        if (!existingOverrides) {
          nextOverrides = undefined;
        } else {
          const stripped = { ...existingOverrides };
          delete stripped.currentVisibilityStateId;
          const stillHas =
            Object.keys(stripped.parameterOverrides).length > 0 ||
            stripped.representationEtag !== undefined ||
            stripped.representationDataHandle !== undefined;
          nextOverrides = stillHas ? stripped : undefined;
        }
      } else {
        nextOverrides = {
          ...(existingOverrides ?? { parameterOverrides: {} }),
          currentVisibilityStateId: trimmed,
        };
      }
      const updatedInsert = {
        ...entity,
        visibilityState: trimmed === "" ? undefined : trimmed,
        ...(nextOverrides
          ? { representationOverrides: nextOverrides }
          : { representationOverrides: undefined }),
      };
      const document = assembleDrawingDocument({
        ...geometryDocument,
        spaces: geometryDocument.spaces.map((s) =>
          s.id === space.id
            ? {
                ...s,
                entities: s.entities.map((e) =>
                  e.id === command.entityId ? updatedInsert : e,
                ),
              }
            : s,
        ),
      });
      const nextSpace = document.spaces.find((s) => s.id === space.id);
      const nextEntity = nextSpace?.entities.find(
        (e) => e.id === command.entityId,
      );
      const mutations = nextEntity
        ? singleUpdated(space.id, entity, nextEntity)
        : NO_MUTATIONS;
      return { document, mutations };
    }

    default:
      return null;
  }
}

